import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import * as Yup from 'yup'
import { Form } from '@carsys/form'
import { useShopCountry } from '@cbgms/base/shop-country'
import { useIsPortal } from 'utils/Subscription/PermissionSelector'
import { IVehicleType } from '@cbgms/api'
import { OdometerType } from '@carsys/enums/odometer-type'
import { ShopCountry } from '@cbgms/base/shop-id'
import toUpper from 'lodash/fp/toUpper'
import { CountryCode } from '@carsys/enums/country-code'
import { useDefaultOrderAccountVehicleProvider } from 'app/vehicles/helpers/use-default-order-account-vehicle-provider'
import * as qp from 'app/vehicles/components/UnknownVehicleV2/query-params'
import { logout, setCurrentShop } from 'app/auth/state/actions'
import { clearEcommerce } from 'utils/TagManager/tag-manager'
import N from 'utils/navigation'
import { useDispatch } from 'react-redux'
import { ActionBeforeLogout } from '@carsys/form/src/BaseNavigationConfirmation'

interface InitialValues {
  initialRegistration?: string
  initialRegistrationCountry?: CountryCode
  initialVIN?: string
  initialOdometerUnit?: OdometerType
  initialOdometerValue?: number
  vehicle: Partial<IVehicleType>
  forcedProvider?: string
  typeSelectMode?: boolean
}

interface FormActions {
  onSubmit: (v: any) => any
  onSubmitSuccess: (v: any) => any
}

export interface UnknownVehicleFormValues {
  Provider: string
  Country?: CountryCode
  Registration: string
  OdometerUnit: OdometerType
  OdometerValue: number
  Vin: string
  vehicle: Partial<IVehicleType & { UUID: string }>
}

interface Props extends InitialValues, FormActions {}

/*
  Form logic for vehicle selection.
  Can be used in combination with UnknownVehicleV2 component.
*/
export const UnknownVehicleV2Form: React.FC<Props> = ({
  initialRegistration,
  initialOdometerUnit,
  initialOdometerValue,
  initialRegistrationCountry,
  initialVIN,
  forcedProvider,
  typeSelectMode,
  vehicle,
  onSubmit,
  onSubmitSuccess,
  children
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { search } = useLocation()
  const isPortal = useIsPortal()

  const { country } = useShopCountry()
  const Country = (initialRegistrationCountry || country || CountryCode.NL) as CountryCode
  const defaultProvider = useDefaultOrderAccountVehicleProvider()

  const initialValues = useMemo(() => {
    // set initial values from search param
    const registration = initialRegistration || new URLSearchParams(search).get(qp.QueryParamRegistration) || ''
    const vin = initialVIN || new URLSearchParams(search).get(qp.QueryParamVIN) || ''

    return {
      Provider: forcedProvider || defaultProvider || '',
      Country,
      Registration: toUpper(registration).replace(/ /g, '').substr(0, 12),
      OdometerUnit: initialOdometerUnit || country === ShopCountry.GB ? OdometerType.Miles : OdometerType.Kilometers,
      OdometerValue: initialOdometerValue || 0,
      Vin: vin || '',
      vehicle: vehicle || {}
    }
  }, [search, initialRegistration, initialRegistrationCountry, initialVIN])

  const validationSchema = useMemo(
    () =>
      Yup.object()
        .shape({
          provider: Yup.string(),
          Vin: Yup.string()
            .min(2, t('global.field_length_min', { value: 2 }))
            .max(17, t('global.field_length_max', { value: 17 })),
          Registration: Yup.string(),
          OdometerUnit: Yup.mixed<OdometerType>().oneOf(Object.values(OdometerType)),
          OdometerValue: Yup.number(),
          Country: Yup.string().required(t('global.field_is_required')),
          vehicle: Yup.object().shape({
            Manufacturer: Yup.string().required(t('global.field_is_required')),
            Model: Yup.string().required(t('global.field_is_required'))
          })
        })
        .test({
          name: 'At least Vin or Registration should be filled',
          test(value) {
            const isInvalid = !value.Vin && !value.Registration
            // if portal than registration or VIN is not required
            if (!isInvalid || isPortal || typeSelectMode) return true
            // we use this from yup, it has nothing to do with the function component
            // eslint-disable-next-line react/no-this-in-sfc
            return this.createError({
              path: 'Vin | Registration',
              message: t('unknown_vehicle.vin_or_registration_required')
            })
          }
        }),
    [t]
  )

  const handleShopChange = async (shopId: string) => {
    clearEcommerce()
    N.push('/')
    dispatch(setCurrentShop(shopId))
  }

  const handleLogout = async (orderAction: `${ActionBeforeLogout}`) => {
    await dispatch(logout())
    if (orderAction === ActionBeforeLogout.OrderDelete) {
      setTimeout(() => {
        N.replace('/auth/login')
        window.location.reload()
      }, 1000)
    } else {
      setTimeout(() => window.location.reload(), 1000)
    }
  }

  return (
    <Form<UnknownVehicleFormValues>
      onSubmit={onSubmit}
      onSubmitSuccess={onSubmitSuccess}
      initialValues={initialValues}
      validationSchema={validationSchema}
      handleShopChange={handleShopChange}
      handleLogout={handleLogout}
      isPortal={isPortal}
      style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      {children}
    </Form>
  )
}
